import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Download, RotateCcw, Wand2, Mail } from 'lucide-react';
import { SIZES, ROOMS, FONTS, FONT_CSS, buildLayers, textLayer } from './brand/brandPresets';
import { BrandCanvas } from './brand/BrandCanvas';
import { AssetLibrary } from './brand/AssetLibrary';
import { LayerControls } from './brand/LayerControls';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });
const h3 = 'font-heading text-[11px] tracking-widest uppercase text-orange-400 mb-2';

if (typeof document !== 'undefined' && !document.getElementById('brand-fonts')) {
  const l = document.createElement('link');
  l.id = 'brand-fonts'; l.rel = 'stylesheet'; l.href = FONT_CSS;
  document.head.appendChild(l);
}

const KEEP = ['text', 'font', 'color', 'stroke', 'backdrop'];
function relayout(old, from, to, fresh) {
  const byRole = Object.fromEntries(old.filter((L) => L.role).map((L) => [L.role, L]));
  const s = Math.min(to.w / from.w, to.h / from.h);
  const built = fresh.filter((L) => byRole[L.role]).map((L) => {
    const o = byRole[L.role];
    return o.type === 'text' ? { ...L, ...Object.fromEntries(KEEP.map((k) => [k, o[k]])) } : L;
  });
  const extras = old.filter((L) => !L.role || L.role === 'logo').map((L) => ({
    ...L, x: Math.min(to.w, L.x * (to.w / from.w)), y: Math.min(to.h, L.y * (to.h / from.h)),
    ...(L.type === 'text' ? { size: L.size * s } : { w: Math.min(L.w * s, to.w) }),
  }));
  return [...built, ...extras];
}

export default function PhotoBrander() {
  const [assets, setAssets] = useState([]);
  const [people, setPeople] = useState(null);
  const [cutting, setCutting] = useState(false);
  const [size, setSize] = useState(SIZES[0]);
  const [room, setRoom] = useState(ROOMS[0]);
  const [bg, setBg] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);

  const loadLib = () => axios.get(`${API}/api/admin/brand/library`, { headers: auth() })
    .then((r) => { setAssets(r.data.assets); return r.data.assets; }).catch(() => toast.error('Could not load library'));
  useEffect(() => { loadLib(); }, []);

  const bgFor = (rm, list = assets) => list.find((a) => a.kind === 'background' && a.name === rm.bg) || list.find((a) => a.kind === 'background');
  const layout = (p = people, rm = room, sz = size) => {
    setLayers(buildLayers(sz, rm, p, assets.filter((a) => a.kind === 'logo'))); setSelected(null);
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setCutting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/api/admin/brand/cutout`, fd, { headers: auth(), timeout: 240000 });
      setPeople(r.data);
      if (!bg) setBg(bgFor(room)?.url || null);
      layout(r.data);
      toast.success('Background removed — drag anything to adjust');
    } catch (err) { toast.error(err?.response?.data?.detail || 'AI cut-out failed'); }
    finally { setCutting(false); }
  };

  const pickRoom = (key) => {
    const rm = ROOMS.find((r) => r.key === key);
    setRoom(rm);
    const b = bgFor(rm);
    if (b) setBg(b.url);
    setLayers((ls) => ls.map((L) => (L.role === 'info' ? { ...L, text: rm.info } : L)));
  };
  const pickSize = (key) => {
    const sz = SIZES.find((s) => s.key === key);
    setLayers((ls) => (ls.length ? relayout(ls, size, sz, buildLayers(sz, room, people, assets.filter((a) => a.kind === 'logo'))) : ls));
    setSize(sz);
  };
  const toggleLogo = (a) => {
    const has = layers.find((L) => L.src === a.url);
    if (has) { setLayers((ls) => ls.filter((L) => L.id !== has.id)); return; }
    const s = Math.min(size.w, size.h);
    const id = `logo-${Date.now()}`;
    setLayers((ls) => [...ls, { id, type: 'image', role: 'logo', src: a.url, w: s * 0.25, x: size.w / 2, y: size.h / 2 }]);
    setSelected(id);
  };
  const update = (id, patch) => setLayers((ls) => ls.map((L) => (L.id === id ? { ...L, ...patch } : L)));
  const order = (dir) => setLayers((ls) => {
    const i = ls.findIndex((L) => L.id === selected), j = i + dir;
    if (i < 0 || j < 0 || j >= ls.length) return ls;
    const n = [...ls]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const addText = () => {
    const L = textLayer({ text: 'NEW TEXT', size: Math.min(size.w, size.h) * 0.06, x: size.w / 2, y: size.h / 2 });
    setLayers((ls) => [...ls, L]); setSelected(L.id);
  };

  const download = () => {
    setSelected(null);
    setTimeout(() => {
      canvasRef.current.toBlob((b) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `oki-escape-${room.key}-${size.key}-${Date.now()}.jpg`;
        a.click();
      }, 'image/jpeg', 0.92);
    }, 300);
  };

  const sendEmail = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error('Enter a valid email'); return; }
    setSelected(null);
    setSending(true);
    setTimeout(() => {
      canvasRef.current.toBlob(async (b) => {
        try {
          const fd = new FormData();
          fd.append('file', b, 'photo.jpg');
          fd.append('email', email.trim());
          await axios.post(`${API}/api/admin/brand/email`, fd, { headers: auth() });
          toast.success(`Photo sent to ${email.trim()}`);
          setEmail('');
        } catch (err) { toast.error(err?.response?.data?.detail || 'Email failed'); }
        finally { setSending(false); }
      }, 'image/jpeg', 0.9);
    }, 300);
  };

  const sel = layers.find((L) => L.id === selected);
  const ready = layers.length > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="photo-brander">
      <div className="flex items-center gap-3 flex-wrap">
        <Wand2 className="text-orange-400" size={22} />
        <h2 className="font-heading text-2xl tracking-widest uppercase">Photo Branding</h2>
        <button onClick={() => fileRef.current?.click()} disabled={cutting} data-testid="brand-upload-photo"
          className="ml-auto inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-xs px-5 py-2.5 rounded-full disabled:opacity-60 transition-colors">
          {cutting ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          {cutting ? 'Removing background…' : people ? 'New photo' : 'Upload group photo'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="brand-photo-input" />
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <button key={s.key} onClick={() => pickSize(s.key)} data-testid={`brand-size-${s.key}`}
                className={`font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors ${size.key === s.key ? 'bg-orange-600 text-black border-orange-500' : 'border-border text-muted-foreground hover:border-orange-500/50'}`}>
                {s.label}
              </button>
            ))}
          </div>
          {ready ? (
            <div className="mx-auto" style={{ maxWidth: size.h > size.w ? 560 : 900 }}>
              <BrandCanvas size={size} bg={bg} layers={layers} selected={selected} canvasRef={canvasRef}
                onSelect={setSelected} onMove={(id, x, y) => update(id, { x, y })} />
            </div>
          ) : (
            <div data-testid="brand-empty" className="aspect-[4/3] rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-3 text-muted-foreground">
              {cutting ? <Loader2 className="animate-spin text-orange-500" size={32} /> : <Camera size={32} />}
              <p className="text-sm">{cutting ? 'AI is cutting out the group (about 10–15 seconds)…' : 'Upload the green-screen group photo to start'}</p>
            </div>
          )}
          {ready && (
            <div className="flex gap-2 justify-end flex-wrap">
              <div className="flex items-center gap-2 mr-auto flex-1 min-w-[260px]">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@email.com"
                  onKeyDown={(e) => e.key === 'Enter' && sendEmail()} data-testid="brand-email-input"
                  className="flex-1 bg-background border border-border rounded-full px-4 py-2 text-sm outline-none focus:border-orange-500" />
                <button onClick={sendEmail} disabled={sending || !email} data-testid="brand-email-send"
                  className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase border border-orange-500 text-orange-400 hover:bg-orange-600 hover:text-black px-4 py-2 rounded-full disabled:opacity-40">
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {sending ? 'Sending…' : 'Email photo'}
                </button>
              </div>
              <button onClick={() => layout()} data-testid="brand-reset-layout"
                className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase border border-border hover:border-orange-500 px-4 py-2 rounded-full"><RotateCcw size={12} /> Reset layout</button>
              <button onClick={download} data-testid="brand-download"
                className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-5 py-2 rounded-full"><Download size={12} /> Download</button>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <section>
            <div className={h3}>Room</div>
            <select value={room.key} onChange={(e) => pickRoom(e.target.value)} data-testid="brand-room-select"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm">
              {ROOMS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </section>
          {ready && (
            <section>
              <div className={h3}>Result</div>
              <div className="flex gap-2">
                {['WE ESCAPED', 'SO CLOSE!', 'NO ESCAPE'].map((t) => {
                  const on = layers.some((L) => L.role === 'title3' && L.text === t);
                  return (
                    <button key={t} data-testid={`brand-result-${t.replace(/[^A-Z]/g, '').toLowerCase()}`}
                      onClick={() => setLayers((ls) => ls.map((L) => (L.role === 'title3' ? { ...L, text: t } : L)))}
                      className={`flex-1 font-heading text-[10px] tracking-widest uppercase px-2 py-2 rounded-full border transition-colors ${on ? 'bg-orange-600 text-black border-orange-500' : 'border-border text-muted-foreground hover:border-orange-500/50'}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
          {ready && (
            <section>
              <div className={h3}>Title font</div>
              <select data-testid="brand-title-font-select" defaultValue="" onChange={(e) => e.target.value && setLayers((ls) => ls.map((L) => (/^title/.test(L.role || '') ? { ...L, font: e.target.value } : L)))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm">
                <option value="" disabled>Change all 3 titles…</option>
                {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
            </section>
          )}
          {ready && (
            <section className="rounded-xl border border-border p-3 bg-card">
              <div className={h3}>Selected layer</div>
              <LayerControls layer={sel} onAddText={addText} onOrder={order}
                onChange={(p) => update(selected, p)} onDelete={() => { setLayers((ls) => ls.filter((L) => L.id !== selected)); setSelected(null); }} />
            </section>
          )}
          <section>
            <div className={h3}>Backgrounds</div>
            <AssetLibrary kind="background" assets={assets} selectedIds={assets.filter((a) => a.url === bg).map((a) => a.id)}
              onPick={(a) => setBg(a.url)} onChanged={loadLib} />
          </section>
          <section>
            <div className={h3}>Logos (tap to add / remove)</div>
            <AssetLibrary kind="logo" assets={assets} selectedIds={assets.filter((a) => layers.some((L) => L.src === a.url)).map((a) => a.id)}
              onPick={toggleLogo} onChanged={loadLib} />
          </section>
        </aside>
      </div>
    </div>
  );
}
